/* istanbul ignore file */
import React from 'react';
import { Grid2 as Grid } from '@mui/material';
import { DateInput, TextInput } from 'framework/form';
import RemoveListItemButton from 'domain/projects/project/weeklyreports/shared/button/RemoveListItemButton';
import useInputRow, { ProjectReportType } from './useInputRow';
import { NumberFieldType } from 'domain/shared/core/constants';
import { TextButton } from 'framework/ui/ui-builder';
import { flexColSx } from 'framework/ui/styles';
import type { DiscoveryScopeBasedType } from './inputTypes';

interface DiscoveryScopeBasedTypesDataInputRowProps {
  data: DiscoveryScopeBasedType;
  id: number;
  isPeriodEditable: boolean;
  formData: any;
  updateFormData: (source: string, newFormData: any) => void;
  getErrorMessages: (fieldSource: string) => string[];
}

export const getDiscoveryScopeBasedTypesItemSource = (id: number) => {
  return `internalReportData.discoveryScopeBasedTypesData[${id}]`;
};

const DiscoveryScopeBasedTypesDataInputRow = ({
  data,
  id,
  formData,
  isPeriodEditable,
  updateFormData,
  getErrorMessages,
}: DiscoveryScopeBasedTypesDataInputRowProps) => {
  const source = getDiscoveryScopeBasedTypesItemSource(id);
  const { getOnChangeCallback, removeRow, getOnChangeNumberFieldCallback } = useInputRow({
    data,
    source,
    formData,
    id,
    updateFormData,
    listSource: 'discoveryScopeBasedTypesData',
    reportType: ProjectReportType.INTERNAL_REPORT,
  });

  return (
    <Grid container spacing={2} style={id === 0 ? { alignItems: 'flex-end' } : {}}>
      <Grid size={{ xs: 12, md: 2 }}>
        <DateInput
          source={`${source}.periodFrom`}
          label={id === 0 ? 'Period from' : ''}
          value={data.periodFrom}
          disabled={data.fromPreviousReport}
          onChangeCallback={getOnChangeCallback('periodFrom')}
          isRequired
          minDate={formData.firstInternalReport ? null : formData.from}
          maxDate={formData.to}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <DateInput
          source={`${source}.periodTo`}
          label={id === 0 ? 'Period to' : ''}
          value={data.periodTo}
          disabled={data.fromPreviousReport && !isPeriodEditable}
          onChangeCallback={getOnChangeCallback('periodTo')}
          isRequired
          minDate={data.fromPreviousReport ? data.periodTo : data.periodFrom}
          maxDate={formData.to}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 1 }}>
        <TextInput
          source={`${source}.scopeCompletion`}
          label={id === 0 ? 'Scope completion, %' : ''}
          value={data.scopeCompletion ? data.scopeCompletion.toString() : '0'}
          onChangeCallback={getOnChangeNumberFieldCallback(NumberFieldType.DOUBLE, 'scopeCompletion')}
          isRequired
        />
      </Grid>
      <Grid sx={flexColSx}>
        <TextInput
          source={`${source}.actualBudget`}
          label={id === 0 ? 'Actual budget, h' : ''}
          value={data.actualBudget ? data.actualBudget.toString() : '0'}
          onChangeCallback={getOnChangeNumberFieldCallback(NumberFieldType.DOUBLE, 'actualBudget')}
          isRequired
        />
      </Grid>
      <Grid size={{ xs: 12, md: 1 }}>
        <TextInput
          source={`${source}.totalBudget`}
          label={id === 0 ? 'Total budget, h' : ''}
          value={data.totalBudget ? data.totalBudget.toString() : '0'}
          disabled={data.fromPreviousReport}
          onChangeCallback={getOnChangeNumberFieldCallback(NumberFieldType.DOUBLE, 'totalBudget')}
          isRequired
        />
      </Grid>
      <Grid sx={flexColSx}>
        <TextInput
          source={`${source}.actualWorkingDays`}
          label={id === 0 ? 'Actual working days' : ''}
          value={data.actualWorkingDays ? data.actualWorkingDays.toString() : '0'}
          onChangeCallback={getOnChangeNumberFieldCallback(NumberFieldType.INTEGER, 'actualWorkingDays')}
          isRequired
        />
      </Grid>
      <Grid size={{ xs: 12, md: 1 }}>
        <TextInput
          source={`${source}.totalWorkingDays`}
          label={id === 0 ? 'Total working days' : ''}
          value={data.totalWorkingDays ? data.totalWorkingDays.toString() : '0'}
          disabled={data.fromPreviousReport}
          onChangeCallback={getOnChangeNumberFieldCallback(NumberFieldType.INTEGER, 'totalWorkingDays')}
          isRequired
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <TextButton
          source={`${source}.comments`}
          value={data.comments}
          label={id === 0 ? 'Comments' : ''}
          title="Comments"
          onChangeCallback={getOnChangeCallback('comments')}
          errors={getErrorMessages(`${source}.comments`)}
        />
      </Grid>
      <Grid
        size={{ xs: 12, md: 1 }}
        sx={{
          marginTop: id === 0 ? '45px' : '',
          alignSelf: id === 0 ? 'center' : '',
        }}
      >
        <RemoveListItemButton isVisible={!data.fromPreviousReport} onClickCallback={removeRow} />
      </Grid>
    </Grid>
  );
};

export default DiscoveryScopeBasedTypesDataInputRow;
